import { useState } from "react";
import { Code2, FileText, Layers, Network, Palette, type LucideIcon } from "lucide-react";
import type { ChatSources, SourceGraph } from "@/types/chat";

// The sources under a chat answer: which graphs it read (one chip each) and the nodes it names. Built by the agents
// service from its tool results, so every chip is something a tool returned for this answer.

const GRAPH: Record<SourceGraph, { label: string; icon: LucideIcon }> = {
  functional: { label: "Functional", icon: Layers },
  code: { label: "Code", icon: Code2 },
  architecture: { label: "Architecture", icon: Network },
  design: { label: "Design", icon: Palette },
  documents: { label: "Documents", icon: FileText },
};
const SHOWN = 6; // cited chips before "+ n more"

const CHIP =
  "inline-flex items-center gap-1 max-w-[260px] px-2 py-0.5 rounded-full border border-surface-border bg-surface " +
  "text-[11px] text-muted";

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function fileName(path: string) {
  return path.split("/").pop() || path;
}

export default function AnswerSources({ sources }: { sources?: ChatSources | null }) {
  const [all, setAll] = useState(false);
  if (!sources?.graphs?.length) return null;
  const cited = sources.cited ?? [];
  const shown = all ? cited : cited.slice(0, SHOWN);

  return (
    <div className="flex flex-col gap-1.5 w-full px-1" aria-label="Sources">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs uppercase tracking-wide text-muted mr-0.5">Sources</span>
        {sources.graphs.map((g) => {
          const meta = GRAPH[g.graph] ?? { label: g.graph, icon: FileText };
          const Icon = meta.icon;
          const repos = g.repositories?.length ? ` · ${g.repositories.join(", ")}` : "";
          return (
            <span
              key={g.graph}
              className={CHIP}
              title={`${meta.label} graph: ${plural(g.calls, "call")}, ${plural(g.nodes, "node")} read${repos}`}
            >
              <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
              <span className="truncate">
                {meta.label}
                {repos}
              </span>
            </span>
          );
        })}
      </div>
      {shown.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {shown.map((c, i) => {
            const Icon = (GRAPH[c.graph] ?? GRAPH.documents).icon;
            const isCode = c.graph === "code" && c.path;
            const text = isCode ? `${fileName(c.path!)}${c.line != null ? `:${c.line}` : ""}` : c.name ?? c.label;
            const where = isCode
              ? [c.path + (c.line != null ? `:${c.line}` : ""), c.name, c.repository && `repository ${c.repository}`]
              : [c.label, c.persona && `persona ${c.persona}`];
            return (
              <span key={`${c.graph}-${c.id ?? c.path}-${i}`} className={CHIP} title={where.filter(Boolean).join(" · ")}>
                <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
                <span className="text-xs opacity-70 shrink-0">{c.label}</span>
                <span className="truncate text-text-secondary">{text}</span>
              </span>
            );
          })}
          {cited.length > SHOWN && (
            <button
              type="button"
              onClick={() => setAll((v) => !v)}
              className="text-[11px] text-muted hover:text-text-primary px-1"
            >
              {all ? "Show less" : `+ ${cited.length - SHOWN} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
